import React from "react";
import { Editor } from "@tiptap/core";
import { Stack, Typography, useTheme } from "@mui/material";
import {
  H1Icon,
  H2Icon,
  H3Icon,
  BulletPointsIcon,
  NumberListIcon,
  ChecklistIcon,
  BlockQuotesIcon,
  CodeIcon,
  TextIcon,
} from "components/icons";

interface NodeTypeOption {
  title: string;
  description: string;
  icon: React.ReactNode;
  isActive: (editor: Editor) => boolean;
  command: (editor: Editor) => void;
}

const nodeTypeOptions: NodeTypeOption[] = [
  {
    title: "Text",
    description: "Paragraph",
    icon: <TextIcon />,
    isActive: (editor) => editor.isActive("paragraph"),
    command: (editor) => {
      editor.chain().focus().setParagraph().run();
    },
  },
  {
    title: "Heading 1",
    description: "Big section heading",
    icon: <H1Icon />,
    isActive: (editor) => editor.isActive("heading", { level: 1 }),
    command: (editor) => {
      editor.chain().focus().setHeading({ level: 1 }).run();
    },
  },
  {
    title: "Heading 2",
    description: "Medium section heading",
    icon: <H2Icon />,
    isActive: (editor) => editor.isActive("heading", { level: 2 }),
    command: (editor) => {
      editor.chain().focus().setHeading({ level: 2 }).run();
    },
  },
  {
    title: "Heading 3",
    description: "Small section heading",
    icon: <H3Icon />,
    isActive: (editor) => editor.isActive("heading", { level: 3 }),
    command: (editor) => {
      editor.chain().focus().setHeading({ level: 3 }).run();
    },
  },
  {
    title: "Bullet List",
    description: "Simple bullet list",
    icon: <BulletPointsIcon />,
    isActive: (editor) => editor.isActive("bulletList"),
    command: (editor) => {
      editor.chain().focus().toggleBulletList().run();
    },
  },
  {
    title: "Numbered List",
    description: "List with numbering",
    icon: <NumberListIcon />,
    isActive: (editor) => editor.isActive("orderedList"),
    command: (editor) => {
      editor.chain().focus().toggleOrderedList().run();
    },
  },
  {
    title: "Checklist",
    description: "Task list with checkboxes",
    icon: <ChecklistIcon />,
    isActive: (editor) => editor.isActive("taskList"),
    command: (editor) => {
      editor.chain().focus().toggleTaskList().run();
    },
  },
  {
    title: "Quote",
    description: "Blockquote",
    icon: <BlockQuotesIcon />,
    isActive: (editor) => editor.isActive("blockquote"),
    command: (editor) => {
      editor.chain().focus().toggleBlockquote().run();
    },
  },
  {
    title: "Code",
    description: "Code block",
    icon: <CodeIcon />,
    isActive: (editor) => editor.isActive("codeBlock"),
    command: (editor) => {
      editor.chain().focus().toggleCodeBlock().run();
    },
  },
];

export const TurnintoDropdown = ({
  editor,
  onClose,
}: {
  editor: Editor;
  onClose?: () => void;
}) => {
  const theme = useTheme();

  return (
    <Stack
      sx={{
        minWidth: "240px",
        maxWidth: "300px",
        bgcolor: "#FFF",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {nodeTypeOptions.map((option, index) => {
        const isActive = option.isActive(editor);
        return (
          <Stack
            key={index}
            direction="row"
            spacing={1.5}
            sx={{
              p: "8px 12px",
              cursor: "pointer",
              alignItems: "center",
              backgroundColor: isActive
                ? theme.palette.neutral["005"]
                : "transparent",
              "&:hover": {
                backgroundColor: theme.palette.neutral["010"],
              },
              transition: "background-color 0.15s ease",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              option.command(editor);
              onClose?.();
            }}
          >
            <Stack
              sx={{
                width: "24px",
                height: "24px",
                alignItems: "center",
                justifyContent: "center",
                color: isActive
                  ? theme.palette.primary.main
                  : theme.palette.lightBg.medium,
              }}
            >
              {option.icon}
            </Stack>
            <Stack flex={1}>
              <Typography
                variant="body-medium"
                sx={{
                  color: isActive
                    ? theme.palette.lightBg.high
                    : theme.palette.lightBg.medium,
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                {option.title}
              </Typography>
              <Typography
                variant="body-small"
                sx={{
                  color: theme.palette.lightBg.low,
                }}
              >
                {option.description}
              </Typography>
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );
};
